import { useState, useCallback, useEffect } from "react";
import { shortcodeProcessor } from "@/services/shortcodeProcessor";
import { useBrandManager } from "@/hooks/useBrandManager";

export const useShortcodeProcessor = () => {
  const { currentBrandId } = useBrandManager();
  const [isProcessing, setIsProcessing] = useState(false);
  const [availablePersonas, setAvailablePersonas] = useState<Array<{ name: string; displayName: string }>>([]);
  const [availablePersonaBriefs, setAvailablePersonaBriefs] = useState<Array<{ name: string; displayName: string }>>([]);

  const processShortcodes = useCallback(async (text: string): Promise<string> => {
    if (!text || !currentBrandId) return text;
    
    setIsProcessing(true);
    try {
      const processedText = await shortcodeProcessor.processShortcodes(text, currentBrandId);
      return processedText;
    } catch (error) {
      console.error('Error processing shortcodes:', error);
      return text;
    } finally {
      setIsProcessing(false);
    }
  }, [currentBrandId]);

  const loadAvailableOptions = useCallback(async () => {
    if (!currentBrandId) return;

    try {
      const [personas, briefs] = await Promise.all([
        shortcodeProcessor.getAvailablePersonas(currentBrandId),
        shortcodeProcessor.getAvailablePersonaBriefs(currentBrandId)
      ]);
      
      setAvailablePersonas(personas);
      setAvailablePersonaBriefs(briefs);
    } catch (error) {
      console.error('Error loading available shortcode options:', error);
    }
  }, [currentBrandId]);

  const hasShortcodes = useCallback((text: string): boolean => {
    const personaRegex = /\{persona:[^}]+\}/;
    const briefRegex = /\{personabrief:[^}]+\}/;
    return personaRegex.test(text) || briefRegex.test(text);
  }, []);

  const insertShortcode = useCallback((type: 'persona' | 'personabrief', name: string, format?: string): string => {
    const formatSuffix = format && format !== 'brief' ? `:${format}` : '';
    return `{${type}:${name}${formatSuffix}}`;
  }, []);

  useEffect(() => {
    loadAvailableOptions();
  }, [loadAvailableOptions]);

  useEffect(() => {
    // Clear cache when brand changes
    shortcodeProcessor.clearCache();
  }, [currentBrandId]);

  return {
    processShortcodes,
    isProcessing,
    availablePersonas,
    availablePersonaBriefs,
    hasShortcodes,
    insertShortcode,
    refreshOptions: loadAvailableOptions
  };
};